import React, {useState} from "react";
import {Alert, Button, Row, Spinner, Table} from "react-bootstrap";
import Container from "react-bootstrap/Container";
import {DatatableWrapper, TableBody, TableColumnType, TableHeader} from "react-bs-datatable";
import {useNavigate} from "react-router-dom";
import notifier from "simple-react-notifications2";

import {useGetLeasePackageCatalogs} from "../../api/catalogs/useLeasePackageCatalogs";
import {useSaveLeasePackageCatalog} from "../../api/catalogs/useSaveLeasePackageCatalog";
import CreateCatalogModal from "../../components/catalog/CreateCatalogModal";
import {LeasePackageCatalogSummaryDto, SaveLeasePackageCatalogDto} from "../../model/LeasePackageCatalog";

type CatalogTableRow = LeasePackageCatalogSummaryDto & {
    button: React.ReactNode;
};

export default function Catalogs() {
    const navigate = useNavigate();
    const {loading, data, error, execute} = useGetLeasePackageCatalogs();
    const {
        loading: loadingSave,
        error: saveError,
        execute: executeSave,
        reset: resetSave,
    } = useSaveLeasePackageCatalog();
    const [showCreateModal, setShowCreateModal] = useState(false);

    const headers: TableColumnType<CatalogTableRow>[] = [
        {title: "Id", prop: "id", isSortable: true, isFilterable: true},
        {title: "Name", prop: "name", isSortable: true, isFilterable: true},
        {
            title: "Status",
            prop: "active",
            isSortable: true,
            cell: row => <>{row.active ? "Active" : "Inactive"}</>,
        },
        {title: "Tenants", prop: "tenantsCount", isSortable: true},
        {title: "Enabled Imports", prop: "importsCount", isSortable: true},
        {
            prop: "button",
            cell: row => (
                <Button
                    variant="outline-primary"
                    size="sm"
                    aria-label={`Open catalog ${row.name}, ID ${row.id}`}
                    onClick={() => navigate(`/catalogs/${row.id}`)}
                >
                    Details
                </Button>
            ),
        },
    ];

    function openCreateModal() {
        resetSave();
        setShowCreateModal(true);
    }

    async function createCatalog(dto: SaveLeasePackageCatalogDto) {
        try {
            await executeSave(dto);
            notifier.success("Catalog created");
            await execute().catch(() => undefined);
            setShowCreateModal(false);
        } catch (e) {
            await execute().catch(() => undefined);
            return Promise.reject(e);
        }
    }

    if(loading && data === undefined) {
        return (
            <Container>
                <p className="mt-5" role="status">
                    <Spinner animation="border" size="sm" className="me-2"/>Loading catalogs...
                </p>
            </Container>
        );
    }

    return (
        <Container>
            <Row className="mt-5 mb-4">
                <h1>Offer Catalogs</h1>
            </Row>
            <Row className="mb-4">
                <Button variant="primary" onClick={openCreateModal} disabled={loading || loadingSave}>
                    Create Catalog
                </Button>
            </Row>
            {error && (
                <Alert variant="danger" role="alert">
                    <p>Catalogs could not be loaded.</p>
                    <Button variant="outline-danger" onClick={() => execute().catch(() => undefined)} disabled={loading}>
                        Retry
                    </Button>
                </Alert>
            )}
            {loading && data !== undefined && (
                <p aria-live="polite"><Spinner animation="border" size="sm" className="me-2" />Refreshing catalogs...</p>
            )}
            {data?.length === 0 && !error ? (
                <Alert variant="info">No catalogs have been created yet.</Alert>
            ) : data ? (
                <Row className="mb-5">
                    <DatatableWrapper body={data as CatalogTableRow[]} headers={headers}>
                        <Table responsive className="custom-table">
                            <TableHeader/>
                            <TableBody/>
                        </Table>
                    </DatatableWrapper>
                </Row>
            ) : null}
            {showCreateModal && (
                <CreateCatalogModal
                    show
                    loading={loadingSave}
                    error={saveError}
                    onHide={() => setShowCreateModal(false)}
                    submitCallback={createCatalog}
                />
            )}
        </Container>
    );
}
